"use client";

import React from "react";

import Image from "next/image";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableTextBlock } from "sanity";
import { AnimatePresence } from "framer-motion";
import { Motion } from "@/components/global/utils/Motion";

import cornerArrow from "@/public/icons/corner-arrow.svg";
import plusIcon from "@/public/icons/Add--large.svg";
import minusIcon from "@/public/icons/Subtract--large.svg";

const answerRichtextClasses =
  "richtext richtext-start prose-p:mb-[.75em] prose-strong:text-black prose-strong:font-medium prose-li:text-black/60 prose-li:text-[2.25rem] [&_::marker]:text-black/70 prose-li:p-0 prose-li:m-0 prose-ul:mb-[.75em] prose-ul:flex prose-ul:flex-col prose-ul:gap-[.25rem] prose-ul:pl-[3.5rem]";

function AccordionItem({
  item,
  index,
  open,
  onToggle,
}: {
  item: { question: string; answer: PortableTextBlock[] };
  index: number;
  open: boolean;
  onToggle: () => void;
}) {
  const answerId = `faq-answer-${index}`;

  return (
    <div className="faq-item flex flex-col bg-[#EDECEB] p-[2.5rem] transition-colors duration-300 hover:bg-[#E7E6E5] sm:p-[3rem]">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={answerId}
        onClick={onToggle}
        className="flex w-full items-center gap-[2rem] text-left"
      >
        <Image
          className="!w-[1.75rem] flex-shrink-0"
          src={cornerArrow}
          alt=""
        />
        <h3 className="grow !mb-0 text-black font-geist !text-[2.25rem] leading-[1.3] uppercase !font-normal">
          {item?.question}
        </h3>

        {/* plus crossfades into minus — both glyphs come from the Figma assets */}
        <span className="relative block h-[3rem] w-[3rem] shrink-0" aria-hidden>
          <Image
            className={cn(
              "absolute inset-0 !h-full !w-full transition-all duration-300",
              open && "rotate-90 opacity-0",
            )}
            src={plusIcon}
            alt=""
          />
          <Image
            className={cn(
              "absolute inset-0 !h-full !w-full transition-all duration-300",
              !open && "-rotate-90 opacity-0",
            )}
            src={minusIcon}
            alt=""
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && item?.answer && (
          <Motion
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div
              id={answerId}
              className={cn(
                answerRichtextClasses,
                "pt-[1rem] prose-p:text-black prose-p:opacity-60 prose-p:leading-[1.4] last:prose-p:mb-0",
              )}
            >
              <PortableText value={item.answer} components={richTextComponent} />
            </div>
          </Motion>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQList({
  faq,
  accordion = false,
  className = "",
}: {
  faq: {
    question: string;
    answer: PortableTextBlock[];
  }[];
  /** collapsible items with a plus/minus toggle; off by default — the other pages list every answer open */
  accordion?: boolean;
  className?: string;
}) {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  if (!faq || faq.length === 0) return null;

  if (accordion) {
    return (
      <div className={cn("relative flex flex-col gap-[1rem]", className)}>
        {faq.map((item, index) => (
          <AccordionItem
            key={index}
            item={item}
            index={index}
            open={openIndex === index}
            onToggle={() =>
              setOpenIndex((current) => (current === index ? null : index))
            }
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("relative flex flex-col gap-[1rem]", className)}>
      {faq.map((item, index) => (
        <div
          key={index}
          className="flex flex-col gap-[1rem] bg-[#EDECEB] p-[2.5rem] sm:p-[3rem] faq-item"
        >
          <div className="flex items-center gap-[1rem] w-full">
            <Image
              className="!w-[1.75rem] flex-shrink-0"
              src={cornerArrow}
              alt="corner arrow icon"
            />
            <h3 className="grow !mb-0 text-black font-geist !text-[2.25rem] leading-[1.3] uppercase !font-normal">
              {item?.question}
            </h3>
          </div>

          {item?.answer && (
            <div className={answerRichtextClasses}>
              <PortableText
                value={item.answer}
                components={richTextComponent}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
